import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { GlobalContext } from 'providers/GlobalProvider';
import { selectedTeamId } from '../common/selectedTeamId';
import { BulkVerificationRunView } from 'api/bitedb/entities/bulkVerificationRun.service';
import {
  DatabaseEntityName,
  lookupDatabaseEntities,
} from 'api/bitedb/entities/databaseEntityLookup.service';
import {
  FAILURE_GROUP_COPY,
  failureGroups,
} from './bulkVerificationRunProgress';
import { formatCount, plural } from './bulkVerificationFormat';

type Lookup =
  | { status: 'loading' }
  | { status: 'ready'; byId: Map<string, DatabaseEntityName> }
  | { status: 'error' };

const FailedEntity: React.FC<{ id: string; lookup: Lookup }> = ({
  id,
  lookup,
}) => {
  if (lookup.status === 'loading') {
    return <li className='bdb-bulk-run-chip is-muted'>Loading…</li>;
  }
  if (lookup.status === 'error') {
    return <li className='bdb-bulk-run-chip is-muted'>Name unavailable</li>;
  }
  const record = lookup.byId.get(id);
  if (!record) {
    return (
      <li className='bdb-bulk-run-chip is-muted'>No longer in this database</li>
    );
  }
  return (
    <li className='bdb-bulk-run-chip'>
      <Link to={`/supplier/${record.entityId}`}>
        {record.name || 'Unnamed entity'}
      </Link>
    </li>
  );
};

/** A finished run's failures, grouped by Check Error category, and its skipped count. */
const BulkVerificationRunFailures: React.FC<{
  run: BulkVerificationRunView;
}> = ({ run }) => {
  const { cur_user } = useContext(GlobalContext);
  const teamId = selectedTeamId(cur_user) ?? '';
  const groups = useMemo(() => failureGroups(run), [run]);
  const [lookup, setLookup] = useState<Lookup>({ status: 'loading' });

  useEffect(() => {
    const ids = run.failures.map((failure) => failure.databaseEntityId);
    if (!ids.length) return undefined;
    let cancelled = false;
    setLookup({ status: 'loading' });
    lookupDatabaseEntities(run.databaseId, ids, teamId).then((found) => {
      if (cancelled) return;
      setLookup(
        found
          ? {
              status: 'ready',
              byId: new Map(found.map((row) => [row.databaseEntityId, row])),
            }
          : { status: 'error' },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [run.databaseId, run.failures, teamId]);

  const unlisted = Math.max(0, run.failed - run.failures.length);

  return (
    <div className='bdb-bulk-run-fails'>
      {groups.map((group) => {
        const copy = FAILURE_GROUP_COPY[group.kind];
        const headingId = `bdb-bulk-run-fails-${group.kind}`;
        return (
          <div
            key={group.kind}
            role='group'
            aria-labelledby={headingId}
            className='bdb-bulk-run-group'
          >
            <div id={headingId} className='bdb-bulk-run-group-title'>
              {copy.title} ({formatCount(group.databaseEntityIds.length)})
            </div>
            <p className='bdb-bulk-run-group-body'>{copy.body}</p>
            <ul className='bdb-bulk-run-chips'>
              {group.databaseEntityIds.map((id) => (
                <FailedEntity key={id} id={id} lookup={lookup} />
              ))}
            </ul>
          </div>
        );
      })}
      {unlisted > 0 && (
        <p className='bdb-bulk-run-group-body'>
          {formatCount(unlisted)} more failed{' '}
          {plural(unlisted, 'entity is', 'entities are')} not listed.
        </p>
      )}
      {run.skipped > 0 && (
        <div
          role='group'
          aria-labelledby='bdb-bulk-run-fails-skipped'
          className='bdb-bulk-run-group'
        >
          <div id='bdb-bulk-run-fails-skipped' className='bdb-bulk-run-group-title'>
            Skipped ({formatCount(run.skipped)})
          </div>
          <p className='bdb-bulk-run-group-body'>
            Nothing left to verify, or the record was already being verified on
            its own page. No charge.
          </p>
        </div>
      )}
    </div>
  );
};

export default BulkVerificationRunFailures;
